import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Favorite as FavoriteIcon,
  Topic as TopicIcon,
} from '@mui/icons-material';
import { Header } from '../components/Header';
import { Sidebar } from '../components/Sidebar';
import { DesktopSidebar } from '../components/DesktopSidebar';
import { WelcomeModal } from '../components/WelcomeModal';
import { ThanksPostsPanel } from '../components/ThanksPostsPanel';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useAuth';
import { useUserCompany } from '../hooks/useAuth';
import { AppColors } from '../theme/colors';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { getBoxTemplate, getHeaderTemplate } from '../constants/industryTemplates';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { company, loading: companyLoading } = useCompany(user?.uid || null);
  const { loading: userCompanyLoading } = useUserCompany(user?.uid || null);
  const isDesktop = useIsDesktop();
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);

  const navigateToPostNew = (type: 'voice' | 'thanks' | 'theme') => {
    navigate(`/post/new?type=${type}`);
  };
  
  // 切り替え完了メッセージ用
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // スマホ版のみ：一度閉じたら同一タブでは表示しない
  // location.key を依存配列に追加して、同じパスへの遷移でも再チェックする
  useEffect(() => {
    if (isDesktop) {
      setWelcomeOpen(false);
      return;
    }
    
    // 強制表示フラグがあれば優先して表示
    const forceOpen = sessionStorage.getItem('welcome_force_open');
    if (forceOpen === 'true') {
      sessionStorage.removeItem('welcome_force_open');
      setWelcomeOpen(true);
      return;
    }
    
    const dismissed = sessionStorage.getItem('welcome_dismissed');
    setWelcomeOpen(!dismissed);
  }, [isDesktop, location.key]);

  // 切り替え完了フラグをチェック
  useEffect(() => {
    const userSwitched = sessionStorage.getItem('user_switched');
    if (userSwitched === 'true') {
      // スナックバーを表示
      setSnackbarOpen(true);
      // フラグをクリア
      sessionStorage.removeItem('user_switched');
    }
  }, []);


  if (authLoading || companyLoading || userCompanyLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: AppColors.surface, width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ヘッダー */}
      <Header 
        onMenuClick={() => setDrawerOpen(true)}
        onSettingsClick={() => navigate('/settings')}
        companyName={company?.companyName}
      />

      {/* モバイル用ドロワー */}
      {!isDesktop && <Sidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} />}

      {/* レイアウトコンテナ（ヘッダーの高さ分のpaddingを追加） */}
      <Box sx={{ display: 'flex', flex: 1, pt: '64px' }}>
        {/* PC版用固定サイドバー */}
        {isDesktop && <DesktopSidebar />}

        {/* メインコンテンツ */}
        <Box sx={{ 
          width: '100%', 
          maxWidth: '100%',
          marginLeft: isDesktop ? '240px' : 0,
        }}>
        {/* ホーム（入口）: PCは説明画面、モバイルはサンクス投稿一覧を表示 */}
        {isDesktop ? (
          <Box sx={{ flex: 1, width: '100%', pb: 10 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 4,
                  // 会社名はヘッダーへ移したため、本文の上にあった会社名の帯（約60px）を外した。
                  // 見出しの位置が上がって詰まって見えないよう、その分を余白で空ける
                  pt: '92px',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
              {/* メッセージヘッダー */}
              <Box sx={{ mb: 5, maxWidth: 700 }}>
                <Typography
                  sx={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: AppColors.textPrimary,
                    mb: 2,
                  }}
                >
                  {getHeaderTemplate(company?.industryType).title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 15,
                    color: AppColors.textSecondary,
                    lineHeight: 1.8,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {getHeaderTemplate(company?.industryType).subtitle}
                </Typography>
              </Box>

              {/* 3つの投稿種別ボックス */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 4,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  maxWidth: 1100,
                }}
              >
                {/* アイデアボックス */}
                <Card
                  sx={{
                    width: isDesktop ? 320 : '100%',
                    maxWidth: 340,
                    borderRadius: 4,
                    border: `2px solid ${AppColors.voiceBoxColor}40`,
                    boxShadow: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: AppColors.voiceBoxColor,
                      boxShadow: `0 6px 24px ${AppColors.voiceBoxColor}30`,
                      transform: 'translateY(-3px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: `${AppColors.voiceBoxColor}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2.5,
                      }}
                    >
                      <CampaignIcon sx={{ color: AppColors.voiceBoxColor, fontSize: 40 }} />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: AppColors.textPrimary,
                        mb: 1.5,
                      }}
                    >
                      {getBoxTemplate(company?.industryType, 'voice').name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: AppColors.textSecondary,
                        mb: 3,
                        minHeight: 65,
                        lineHeight: 1.7,
                      }}
                    >
                      {getBoxTemplate(company?.industryType, 'voice').description}
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        navigateToPostNew('voice');
                      }}
                      sx={{
                        bgcolor: AppColors.voiceBoxColor,
                        color: 'white',
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: 15,
                        py: 1.5,
                        '&:hover': {
                          bgcolor: AppColors.primaryDark,
                        },
                      }}
                    >
                      投稿する
                    </Button>
                  </CardContent>
                </Card>

                {/* サンクスボックス */}
                <Card
                  sx={{
                    width: isDesktop ? 320 : '100%',
                    maxWidth: 340,
                    borderRadius: 4,
                    border: `2px solid ${AppColors.thanksBoxColor}40`,
                    boxShadow: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: AppColors.thanksBoxColor,
                      boxShadow: `0 6px 24px ${AppColors.thanksBoxColor}30`,
                      transform: 'translateY(-3px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: `${AppColors.thanksBoxColor}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2.5,
                      }}
                    >
                      <FavoriteIcon sx={{ color: AppColors.thanksBoxColor, fontSize: 40 }} />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: AppColors.textPrimary,
                        mb: 1.5,
                      }}
                    >
                      {getBoxTemplate(company?.industryType, 'thanks').name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: AppColors.textSecondary,
                        mb: 3,
                        minHeight: 65,
                        lineHeight: 1.7,
                      }}
                    >
                      {getBoxTemplate(company?.industryType, 'thanks').description}
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        navigateToPostNew('thanks');
                      }}
                      sx={{
                        bgcolor: AppColors.thanksBoxColor,
                        color: 'white',
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: 15,
                        py: 1.5,
                        '&:hover': {
                          bgcolor: '#E6C99F',
                        },
                      }}
                    >
                      投稿する
                    </Button>
                  </CardContent>
                </Card>

                {/* テーマボックス */}
                <Card
                  sx={{
                    width: isDesktop ? 320 : '100%',
                    maxWidth: 340,
                    borderRadius: 4,
                    border: `2px solid ${AppColors.themeBoxColor}40`,
                    boxShadow: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: AppColors.themeBoxColor,
                      boxShadow: `0 6px 24px ${AppColors.themeBoxColor}30`,
                      transform: 'translateY(-3px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: `${AppColors.themeBoxColor}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2.5,
                      }}
                    >
                      <TopicIcon sx={{ color: AppColors.themeBoxColor, fontSize: 40 }} />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: AppColors.textPrimary,
                        mb: 1.5,
                      }}
                    >
                      {getBoxTemplate(company?.industryType, 'theme').name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: AppColors.textSecondary,
                        mb: 3,
                        minHeight: 65,
                        lineHeight: 1.7,
                      }}
                    >
                      {getBoxTemplate(company?.industryType, 'theme').description}
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        navigateToPostNew('theme');
                      }}
                      sx={{
                        bgcolor: AppColors.themeBoxColor,
                        color: 'white',
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: 15,
                        py: 1.5,
                        '&:hover': {
                          bgcolor: '#E6A3AC',
                        },
                      }}
                    >
                      投稿する
                    </Button>
                  </CardContent>
                </Card>
              </Box>

              {/* 注釈 */}
              <Box
                sx={{
                  mt: 5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: AppColors.primary,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 13,
                    color: AppColors.textTertiary,
                    letterSpacing: 0.5,
                  }}
                >
                  匿名AI補正対応
                </Typography>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: AppColors.primary,
                  }}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, width: '100%', p: 2 }}>
            {company?.id ? <ThanksPostsPanel companyId={company.id} /> : null}
          </Box>
        )}
        </Box>
      </Box>


      {/* ウェルカムモーダル（スマホ表示のみ / 一度閉じたら同一タブでは非表示） */}
      {!isDesktop && (
        <WelcomeModal
          open={welcomeOpen}
          onClose={() => {
            sessionStorage.setItem('welcome_dismissed', 'true');
            setWelcomeOpen(false);
          }}
          industryType={company?.industryType}
        />
      )}

      {/* スナックバー（Flutter版と同じスタイル） */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: 2 }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{
            width: 300,
            maxWidth: '90%',
            bgcolor: '#424242',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white',
            },
          }}
        >
          一般ユーザーに切り替えました
        </Alert>
      </Snackbar>
    </Box>
  );
};